import { useState, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { useUser } from '@/contexts/UserContext';

const STORAGE_KEY = 'forkast-ios-install-dismissed';

interface UseIOSInstallPromptResult {
  showPrompt: boolean;
  dismissPrompt: () => void;
  isIOS: boolean;
  isStandalone: boolean;
  isSafari: boolean;
}

export function useIOSInstallPrompt(): UseIOSInstallPromptResult {
  const { isAuthenticated } = useUser();
  const [dismissed, setDismissed] = useLocalStorage(STORAGE_KEY, false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detect iOS (iPhone, iPad, iPod)
    const iosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(iosDevice);

    // Detect Safari vs Chrome on iOS
    // Safari on iOS doesn't have "CriOS" (Chrome iOS) in user agent
    const safari = iosDevice && !userAgent.includes('crios');
    setIsSafari(safari);

    // Check if running as standalone PWA
    const standalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true;
    setIsStandalone(standalone);
  }, []);

  const dismissPrompt = () => {
    setDismissed(true);
  };

  // Show prompt only for: authenticated + iOS + NOT already installed + NOT dismissed
  const showPrompt = isAuthenticated && isIOS && !isStandalone && !dismissed;

  return {
    showPrompt,
    dismissPrompt,
    isIOS,
    isStandalone,
    isSafari,
  };
}
